import { Injectable } from '@angular/core';

import { storeService } from '@app/modules/store';
import { syncService } from '@app/modules/sync';
import { viewService } from '@app/modules/view';

import { GenericUtils } from '@app/app.commonutils';

const enum LogsLevel {
    Error= "Error",
    Warn= "Warn",
    Info= "Info",
    Debug= "Debug",
    Trace= "Trace"
}

@Injectable()
export class logsService {
    private _loggerUrl = 'logger/writer.php';
    private _lsource: string = null;
    private _authtok: string = null;

    // Default to false so console.* calls don't accumulate in _queued
    // before Start() runs. Was 'true' — which silently kept a growing
    // array in memory even when the logger was disabled, because the
    // console proxy installed in the constructor always called Queue().
    private _started = false;
    private _flush_subscription = null;
    private _queued: Array<any> = [];

    // Generation token to make Start()/Stop() race-safe. Start() is
    // async and is called without await (constructor, LoggerCampaign
    // setter). Bumped on every Start() and Stop(); a Start() that finds
    // the token changed after its INI round-trip abandons its side
    // effects so it can't re-install Flush / re-persist the campaign
    // after a Stop() that ran while it was awaiting.
    private _runtoken = 0;

    get Campaign(){
        return this._lsource;
    }

    private _session = GenericUtils.uuidv4();

    constructor(private store: storeService, private sync: syncService, private view: viewService){
        window.console = this.OverrideConsole();

        let _active = this.store.GetLoggerCampaign();
        if (_active){
            this.Start(_active);
        }
    }

    async Start(campaign){
        this._lsource = campaign;
        if (this._lsource === null){
            return;     // mnust be in a campaign
        }

        this._started = true;
        let _token = ++this._runtoken;

        let _data = await this.sync.DoRequest(this._loggerUrl, null, {
            command: 'INI',
            session: this._session,
            source: this._lsource
        }, false);

        if (_token !== this._runtoken){
            return;     // Stop() (or a newer Start()) ran while awaiting INI
        }

        if (_data['errorcode'] == 0){
            this._authtok = _data['authtok'];
        }
        
        if(this._flush_subscription != null){
            this._flush_subscription.unsubscribe();
            this._flush_subscription = null;
        }

        this._flush_subscription = this.sync.Clock.OnOneSecondTick.subscribe(
        data => {
            this.Flush();
        });

        this.store.SetLoggerCampaign(campaign);
    }

    async Stop(){
        this._runtoken++;   // invalidate any Start() still awaiting INI

        if(this._flush_subscription != null){
            this._flush_subscription.unsubscribe();
            this._flush_subscription = null;
        }

        this._started = false;
        this._lsource = null;
        this._queued = [];              // drop pending entries

        // Clear the cached campaign so the logger does NOT auto-restart
        // on the next app boot from the stale localStorage value. Was
        // the root of 'I disabled the checkbox but logs keep being sent
        // after a reload' — Stop() used to leave the local store as-is
        // and the constructor picked the old value up and called Start().
        this.store.SetLoggerCampaign(null);

        if (this._authtok === null){
            return;     // not initialized
        }

        let _data = await this.sync.DoRequest(this._loggerUrl, null, {
            command: 'END',
            session: this._session,
            authtok: this._authtok
        }, false);

        if (_data['errorcode'] == 0){
            this._authtok = null;
        }
    }

    Queue(level: LogsLevel, message: string, extras: any){
        if (this._started){
            this._queued.push({
                level: level,
                message: message,
                extras: extras
            });    
        }
    }

    private async Flush(){
        if (!this.view.IsOnline){
            return;     // we are offline
        }

        if ((this._authtok !== null) && (this._queued.length > 0)){
            // first try to send the log with the authtoken
            let _first = await this.sync.DoRequest(this._loggerUrl, null, { 
                command: 'LOG', 
                session: this._session,
                authtok: this._authtok, 
                logs: this._queued
             }, false);

            // if authtoken is expired then refresh and send the log
            if ((_first['errorcode'] == 9) || (_first['errorcode'] == 10)) {
                let _retry = await this.sync.DoRequest(this._loggerUrl, null, {
                    command: 'INI',
                    session: this._session,
                    source: this._lsource,
                    logs: this._queued
                }, false);

                if (_retry['errorcode'] != 0){
                    console.error("[LOGGER]: Could not resend log traces (errorcode: " + _retry['errorcode']+ ")");
                }
                else if (_retry['authtok']){
                    // Capture the newly issued token — without this the
                    // next Flush reuses the expired one, loops back into
                    // this retry branch every second, and after ~40s the
                    // server stops accepting (rate limit / session pool
                    // exhaustion) so logs silently stop being sent.
                    this._authtok = _retry['authtok'];
                }
            }
        }

        this._queued = [];
    }

    private OverrideConsole(): Console {
        const _console = console;

        function send_report(service: logsService, level: LogsLevel, message: string, extras: any) {
            service.Queue(level, message, extras);
        }
    
        function process_args(args: any[]) {
            let args_named = {};
            for (let i = 0; i < args.length; i++) {
                switch (typeof args[i]) {
                    case 'object':
                        for (let key in args[i]) {
                            args_named[key] = args[i][key]?.toString();
                        }
                        break;
                    default:
                        args_named[`arg_${i}`] = args[i]?.toString();
                }
    
            }
            return args_named;
        }
    
        // Trazas internas/verbosas: en PRODUCCIÓN se SILENCIAN (ni consola ni
        // envío al server) para no saturar el log; en DESARROLLO (localhost) se
        // mantienen. Override manual con localStorage 'upp_debug': '1' fuerza
        // verbose (aunque sea prod), '0' fuerza silencio (aunque sea dev). No
        // afecta a warn/error ni al flujo de cobro/cajón (esos siempre salen).
        const _MUTED_PREFIXES = [
            '[COMMIT]', '[STORAGE]', '[STORE]', '[SYNCHRONIZE]', '[GLOBAL MUTEX]',
            '[SESSION]', '[VIEW REFRESH]', '[TICKET LIST]', '[INITIALIZE]',
            '[DEVICE] applied forced mode',
            '[TICKET] Wait for last commit', '[TICKET] Keep ticket changes',
            '[TICKET] On drawer check',
            '[WSL] Keepalive', '[WSL] Websocket connection ready',
            '[SMARTDRAWER] Keepalive', '[SMARTDRAWER] GET_QUEUE',
            '[SMARTDRAWER] Success message already processed',
            '[PRINT] Loaded stylesheet', '[PRINT] pre-loading',
        ];
        // Por defecto: verbose SOLO en un navegador de desarrollo local (no
        // empaquetado), silenciado en el resto (PWA en dominio, y app nativa).
        // El flag 'upp_debug' lo fuerza a mano ('1' verbose, '0' silencio).
        // OJO: `environment.production` está en `false` en AMBOS envs a propósito
        // en este proyecto, así que no sirve como flag de build; distinguimos
        // "desarrollo" como localhost/127.0.0.1 NO nativo.
        let _debugLogs = false;
        try {
            const _flag = (typeof localStorage !== 'undefined') ? localStorage.getItem('upp_debug') : null;
            if (_flag === '1'){ _debugLogs = true; }
            else if (_flag === '0'){ _debugLogs = false; }
            else {
                const _host = (typeof location !== 'undefined' && location.hostname) ? location.hostname : '';
                const _proto = (typeof location !== 'undefined' && location.protocol) ? location.protocol : '';
                // El WebView nativo (Cordova/Capacitor) también sirve desde
                // localhost o file:, así que "hostname==localhost" NO basta: en el
                // TPV nativo de PRODUCCIÓN el hostname es localhost igualmente y no
                // debe activar verbose. Excluimos el runtime nativo.
                const _w: any = (typeof window !== 'undefined') ? window : null;
                const _isNative = !!(_w && (_w.cordova || _w.Capacitor)) || _proto === 'file:';
                _debugLogs = !_isNative && (_host === 'localhost' || _host === '127.0.0.1');
            }
        } catch (_e) {}
        const _isMuted = (message: any): boolean => {
            if (_debugLogs || typeof message !== 'string') { return false; }
            for (let i = 0; i < _MUTED_PREFIXES.length; i++){
                if (message.lastIndexOf(_MUTED_PREFIXES[i], 0) === 0) { return true; }   // startsWith
            }
            return false;
        };

        return {
            ..._console,
            log: (message: string, ...args: any[]) => {
                if (_isMuted(message)) { return; }
                _console.log(message, ...args);
                send_report(this, LogsLevel.Trace,  message, process_args(args));
            },
            error: (message: string, ...args: any[]) => {
                _console.error(message, ...args);
                send_report(this, LogsLevel.Error, message, process_args(args));
            },
            warn: (message: string, ...args: any[]) => {
                _console.warn(message, ...args);
                send_report(this, LogsLevel.Warn, message, process_args(args));
            },
            info: (message: string, ...args: any[]) => {
                if (_isMuted(message)) { return; }
                _console.info(message, ...args);
                send_report(this, LogsLevel.Info, message, process_args(args));
            },
            debug: (message: string, ...args: any[]) => {
                if (_isMuted(message)) { return; }
                _console.log(message, ...args);
                send_report(this, LogsLevel.Debug, message, process_args(args));
            },
            trace: (message: string, ...args: any[]) => {
                if (_isMuted(message)) { return; }
                _console.log(message, ...args);
                send_report(this, LogsLevel.Trace, message, process_args(args));
            }
        } as Console
    }
}
